import React, { Component } from 'react';
import { Link } from 'react-router-dom';
type Props = {
  className?: string;
  inverted?: boolean;
  size?: number;
};
/**
 * Six logo concepts for peptides.ml, each committed to a single
 * strong idea. SVG-only craft — every glyph is a hand-tuned path
 * rather than rendered HTML text, so kerning, proportions, and
 * negative space are precisely controlled.
 *
 * Palette: foreground #0F1410, primary green via tailwind `fill-primary`,
 * muted card / secondary as accents.
 */
const PRIMARY = 'fill-primary';
/* ============================================================
 * L1 — The Counter
 * A bespoke "p" monogram where the bowl is a perfect circle
 * containing a primary-green disc — the ".ml" rendered as a
 * literal dot inside the letter. Standalone mark + tight
 * wordmark beside it. The "p" is custom-drawn (not Geist).
 * ============================================================ */
export function LogoV1({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex items-center gap-3.5 group ${className}`} aria-label="peptides.ml" data-id="element-406">
      <svg viewBox="0 0 56 64" style={{
      height: size * 2,
      width: 'auto'
    }} aria-hidden data-id="element-407">
        {/* Custom "p" — bowl + descender as one shape */}
        <path d="M 6 8
             L 6 60
             L 14 60
             L 14 38
             L 28 38
             C 38 38, 46 30, 46 22
             C 46 13, 38 6, 28 6
             L 6 6
             Z
             M 14 14
             L 28 14
             C 32 14, 36 17.5, 36 22
             C 36 26.5, 32 30, 28 30
             L 14 30
             Z" fill={fg} data-id="element-408" />
        {/* Primary-green dot inside the bowl */}
        <circle cx="28" cy="22" r="5" className={PRIMARY} data-id="element-409" />
      </svg>

      {/* Wordmark */}
      <svg viewBox="0 0 230 36" style={{
      height: size * 1.15,
      width: 'auto'
    }} aria-hidden data-id="element-410">
        <defs data-id="element-411">
          <style data-id="element-412">{`.l1-text { font-family: 'Geist', system-ui, sans-serif; font-weight: 500; }`}</style>
        </defs>
        <text x="0" y="28" className="l1-text" fontSize="32" fill={fg} letterSpacing="-0.8" data-id="element-413">
          peptides
        </text>
        <text x="142" y="28" className="l1-text fill-primary" fontSize="32" letterSpacing="-0.8" data-id="element-414">
          .ml
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L2 — The Aliquot
 * The wordmark with the period in ".ml" rendered as a precise
 * primary-green dot — slightly oversized, perfectly placed at
 * x-height. The dot is the brand symbol. Negative space and
 * weight composition do all the work; no icon needed.
 * ============================================================ */
export function LogoV2({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-415">
      <svg viewBox="0 0 320 48" style={{
      height: size * 1.5,
      width: 'auto'
    }} aria-hidden data-id="element-416">
        <defs data-id="element-417">
          <style data-id="element-418">{`
            .l2-word { font-family: 'Geist', system-ui, sans-serif; font-weight: 600; letter-spacing: -1.6px; }
          `}</style>
        </defs>

        {/* peptides */}
        <text x="0" y="34" className="l2-word" fontSize="40" fill={fg} data-id="element-419">
          peptides
        </text>

        {/* The big primary-green dot — the brand mark */}
        <circle cx="184" cy="29" r="7" className={PRIMARY} data-id="element-420" />

        {/* ml — same scale as wordmark */}
        <text x="198" y="34" className="l2-word" fontSize="40" fill={fg} data-id="element-421">
          ml
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L3 — The Masthead
 * A newspaper-masthead style two-line lockup. "PEPTIDES" set
 * in heavy display caps, beneath it a hairline rule + small
 * mono spec line ".ml — RESEARCH GRADE PEPTIDES, UK". Reads
 * as a serious editorial title rather than a startup logo.
 * ============================================================ */
export function LogoV3({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  const muted = inverted ? '#FAF8F2' : '#6E7270';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-422">
      <svg viewBox="0 0 400 78" style={{
      height: size * 2.4,
      width: 'auto'
    }} aria-hidden data-id="element-423">
        <defs data-id="element-424">
          <style data-id="element-425">{`
            .l3-display {
              font-family: 'Geist', system-ui, sans-serif;
              font-weight: 700;
              letter-spacing: 6px;
            }
            .l3-meta {
              font-family: 'Geist Mono', ui-monospace, monospace;
              font-weight: 500;
              letter-spacing: 2.2px;
            }
          `}</style>
        </defs>

        {/* PEPTIDES */}
        <text x="200" y="36" textAnchor="middle" className="l3-display" fontSize="42" fill={fg} data-id="element-426">
          PEPTIDES
        </text>

        {/* Hairline rule */}
        <line x1="20" y1="50" x2="380" y2="50" stroke={fg} strokeWidth="1" opacity="0.45" data-id="element-427" />
        <circle cx="200" cy="50" r="3" className={PRIMARY} data-id="element-428" />

        {/* Spec line — mono caps */}
        <text x="200" y="68" textAnchor="middle" className="l3-meta" fontSize="10" fill={muted} data-id="element-429">
          .ML — RESEARCH GRADE · UK
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L4 — Reversed Authority
 * The wordmark set in confident sans, in white, inside a
 * primary-green pill-shaped block. Single high-contrast
 * statement. The wordmark IS the mark. Brand-aware enough
 * to read everywhere it appears.
 * ============================================================ */
export function LogoV4({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-430">
      <svg viewBox="0 0 280 64" style={{
      height: size * 2,
      width: 'auto'
    }} aria-hidden data-id="element-431">
        <defs data-id="element-432">
          <style data-id="element-433">{`
            .l4-word {
              font-family: 'Geist', system-ui, sans-serif;
              font-weight: 600;
              letter-spacing: -1.4px;
            }
          `}</style>
        </defs>

        {/* Primary-green pill */}
        <rect x="2" y="2" width="276" height="60" rx="30" className={PRIMARY} data-id="element-434" />

        {/* Subtle inner highlight (1px row at top for dimension) */}
        <rect x="2" y="2" width="276" height="2" rx="1" fill="#FFFFFF" opacity="0.18" data-id="element-435" />

        {/* Wordmark in white */}
        <text x="140" y="42" textAnchor="middle" className="l4-word" fontSize="38" fill="#FFFFFF" data-id="element-436">
          peptides
        </text>
        <text x="247" y="42" textAnchor="middle" className="l4-word" fontSize="38" fill="#FFFFFF" opacity="0.7" data-id="element-437">
          .ml
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L5 — Specimen Card
 * A horizontal "lab ID card" lockup. Hairline frame, wordmark
 * tuned in the left field, vertical hairline divider, small
 * mono "credentials block" on the right with batch metadata
 * and a primary-green verified check.
 * ============================================================ */
export function LogoV5({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  const muted = inverted ? '#FAF8F2' : '#6E7270';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-438">
      <svg viewBox="0 0 420 78" style={{
      height: size * 2.4,
      width: 'auto'
    }} aria-hidden data-id="element-439">
        <defs data-id="element-440">
          <style data-id="element-441">{`
            .l5-word {
              font-family: 'Geist', system-ui, sans-serif;
              font-weight: 600;
              letter-spacing: -1.6px;
            }
            .l5-meta {
              font-family: 'Geist Mono', ui-monospace, monospace;
              font-weight: 600;
              letter-spacing: 1.4px;
            }
          `}</style>
        </defs>

        {/* Frame */}
        <rect x="2" y="2" width="416" height="74" rx="6" fill="none" stroke={fg} strokeWidth="1.2" opacity="0.55" data-id="element-442" />

        {/* Wordmark left */}
        <text x="22" y="50" className="l5-word" fontSize="38" fill={fg} data-id="element-443">
          peptides
          <tspan className="fill-primary" data-id="element-444">.ml</tspan>
        </text>

        {/* Vertical divider */}
        <line x1="280" y1="14" x2="280" y2="64" stroke={fg} strokeWidth="1" opacity="0.3" data-id="element-445" />

        {/* Credentials block right */}
        <text x="300" y="30" className="l5-meta" fontSize="9.5" fill={muted} data-id="element-446">
          RG-2024 · UK
        </text>
        <text x="300" y="44" className="l5-meta" fontSize="9.5" fill={muted} data-id="element-447">
          BATCH 0157
        </text>
        <text x="300" y="58" className="l5-meta" fontSize="9.5" fill={muted} data-id="element-448">
          99.7% HPLC
        </text>

        {/* Verified check — primary green disc */}
        <circle cx="395" cy="44" r="11" className={PRIMARY} data-id="element-449" />
        <path d="M 389 45 L 393 49 L 401 41" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" data-id="element-450" />
      </svg>
    </Link>;
}
/* ============================================================
 * L6 — The Refined Wordmark
 * Just the wordmark. No icon, no frame, no decoration. Every
 * letterform precisely tuned: tightened kerning between
 * letterpairs, the period in ".ml" rendered as a perfect
 * circle in primary green, baseline subtly aligned. This is
 * what you'd ship if you had the discipline to ship only one.
 * ============================================================ */
export function LogoV6({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-451">
      <svg viewBox="0 0 240 40" style={{
      height: size * 1.4,
      width: 'auto'
    }} aria-hidden data-id="element-452">
        <defs data-id="element-453">
          <style data-id="element-454">{`
            .l6-word {
              font-family: 'Geist', system-ui, sans-serif;
              font-weight: 500;
              letter-spacing: -1.5px;
            }
          `}</style>
        </defs>

        {/* peptides */}
        <text x="0" y="30" className="l6-word" fontSize="36" fill={fg} data-id="element-455">
          peptides
        </text>

        {/* Custom primary-green dot — replaces typeset period */}
        <circle cx="142" cy="26" r="3.6" className={PRIMARY} data-id="element-456" />

        {/* ml */}
        <text x="150" y="30" className="l6-word" fontSize="36" fill={fg} data-id="element-457">
          ml
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L7 — The Sphere
 * A single primary-green sphere — drawn with a precise hairline
 * equator — paired with a tight wordmark. The sphere reads as
 * a vial cap viewed top-down, or a perfect aliquot drop. Pure
 * geometric authority, no chemistry-icon cliché.
 * ============================================================ */
export function LogoV7({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex items-center gap-3.5 group ${className}`} aria-label="peptides.ml" data-id="element-458">
      <svg viewBox="0 0 56 56" style={{
      height: size * 1.8,
      width: size * 1.8
    }} aria-hidden data-id="element-459">
        <circle cx="28" cy="28" r="24" className="fill-primary" data-id="element-460" />
        {/* hairline equator */}
        <line x1="6" y1="28" x2="50" y2="28" stroke="#FFFFFF" strokeWidth="1" opacity="0.45" data-id="element-461" />
        {/* small N-pole tick */}
        <circle cx="28" cy="10" r="1.4" fill="#FFFFFF" opacity="0.7" data-id="element-462" />
      </svg>

      <svg viewBox="0 0 230 36" style={{
      height: size * 1.2,
      width: 'auto'
    }} aria-hidden data-id="element-463">
        <defs data-id="element-464">
          <style data-id="element-465">{`.l7-text { font-family: 'Geist', system-ui, sans-serif; font-weight: 500; letter-spacing: -1.4px; }`}</style>
        </defs>
        <text x="0" y="28" className="l7-text" fontSize="32" fill={fg} data-id="element-466">
          peptides
        </text>
        <text x="142" y="28" className="l7-text fill-primary" fontSize="32" data-id="element-467">
          .ml
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L8 — The Bisect
 * The wordmark with a precise primary-green horizontal hairline
 * cutting through the optical centre — references a metric
 * line on lab glassware. The line visually anchors the type
 * without compromising readability.
 * ============================================================ */
export function LogoV8({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-468">
      <svg viewBox="0 0 320 56" style={{
      height: size * 1.7,
      width: 'auto'
    }} aria-hidden data-id="element-469">
        <defs data-id="element-470">
          <style data-id="element-471">{`.l8-word { font-family: 'Geist', system-ui, sans-serif; font-weight: 600; letter-spacing: -1.6px; }`}</style>
        </defs>

        {/* Bisecting hairline — full width with primary green centre */}
        <line x1="0" y1="30" x2="60" y2="30" stroke={fg} strokeWidth="1" opacity="0.35" data-id="element-472" />
        <line x1="60" y1="30" x2="260" y2="30" className="stroke-primary" strokeWidth="1.3" data-id="element-473" />
        <line x1="260" y1="30" x2="320" y2="30" stroke={fg} strokeWidth="1" opacity="0.35" data-id="element-474" />

        {/* End-cap ticks */}
        <line x1="0" y1="26" x2="0" y2="34" stroke={fg} strokeWidth="1" opacity="0.55" data-id="element-475" />
        <line x1="320" y1="26" x2="320" y2="34" stroke={fg} strokeWidth="1" opacity="0.55" data-id="element-476" />

        {/* Wordmark — sat directly on the line, intersecting it */}
        <text x="160" y="36" textAnchor="middle" className="l8-word" fontSize="40" fill={fg} data-id="element-477">
          peptides
          <tspan className="fill-primary" data-id="element-478">.ml</tspan>
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L9 — Mono Block
 * The entire logo as a single tight block of mono caps —
 * PEPTIDES.ML with the .ML in primary green, tracked tight,
 * baseline-aligned to a hairline. Vignelli-era pharmaceutical
 * brand confidence. Single line, single weight, single colour.
 * ============================================================ */
export function LogoV9({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex flex-col items-start group ${className}`} aria-label="peptides.ml" data-id="element-479">
      <svg viewBox="0 0 360 36" style={{
      height: size * 1.1,
      width: 'auto'
    }} aria-hidden data-id="element-480">
        <defs data-id="element-481">
          <style data-id="element-482">{`.l9-text { font-family: 'Geist Mono', ui-monospace, monospace; font-weight: 700; letter-spacing: 4px; }`}</style>
        </defs>

        <text x="0" y="26" className="l9-text" fontSize="28" fill={fg} data-id="element-483">
          PEPTIDES
        </text>
        <text x="186" y="26" className="l9-text fill-primary" fontSize="28" data-id="element-484">
          .ML
        </text>
      </svg>

      <svg viewBox="0 0 360 12" style={{
      height: size * 0.4,
      width: 'auto',
      marginTop: 4
    }} aria-hidden data-id="element-485">
        <defs data-id="element-486">
          <style data-id="element-487">{`.l9-meta { font-family: 'Geist Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 2.4px; }`}</style>
        </defs>
        <line x1="0" y1="3" x2="360" y2="3" stroke={fg} strokeWidth="0.8" opacity="0.35" data-id="element-488" />
        <text x="0" y="11" className="l9-meta" fontSize="8" fill={fg} opacity="0.55" data-id="element-489">
          RESEARCH GRADE · UK · COLD-CHAIN
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L10 — The Signature
 * A custom SVG signature — peptides.ml drawn as a continuous
 * flowing path. References a researcher signing a lab
 * notebook entry. Hand-feel, but precisely curved bezier work.
 * ============================================================ */
export function LogoV10({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex items-center group ${className}`} aria-label="peptides.ml" data-id="element-490">
      <svg viewBox="0 0 280 70" style={{
      height: size * 2,
      width: 'auto'
    }} aria-hidden data-id="element-491">
        {/* Stylised signature stroke — single fluid path */}
        <path d="M 8 50
             Q 14 30 22 48
             T 38 48
             Q 44 36 50 50
             Q 58 44 62 50
             T 80 50
             Q 92 30 102 50
             Q 112 40 120 50
             Q 128 30 138 50
             T 158 50
             Q 166 40 174 50
             L 180 50
             Q 186 36 192 50
             T 214 50" fill="none" stroke={fg} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-492" />
        {/* Decorative descender flourish under the signature */}
        <path d="M 8 56
             Q 60 64 120 58
             T 240 56" fill="none" stroke={fg} strokeWidth="1" strokeLinecap="round" opacity="0.35" data-id="element-493" />

        {/* The .ml — primary-green stamped notation beside */}
        <circle cx="232" cy="48" r="3.5" className="fill-primary" data-id="element-494" />
        <text x="240" y="53" fontFamily="Geist Mono, ui-monospace, monospace" fontWeight="700" fontSize="18" className="fill-primary" letterSpacing="1" data-id="element-495">
          ml
        </text>

        {/* Tiny bottom annotation */}
        <text x="8" y="68" fontFamily="Geist Mono, ui-monospace, monospace" fontWeight="500" fontSize="7.5" fill={fg} opacity="0.45" letterSpacing="1.6" data-id="element-496">
          PEPTIDES — LAB-NOTED
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L11 — The Cylinder
 * A bespoke architectural vial — minimal essential lines only.
 * Primary-green crimp cap, hairline glass body, a single
 * meniscus line. Iconic without literalism. Paired with a
 * tight wordmark. Could become the brand's signature mark.
 * ============================================================ */
export function LogoV11({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex items-center gap-3.5 group ${className}`} aria-label="peptides.ml" data-id="element-497">
      <svg viewBox="0 0 32 64" style={{
      height: size * 2.2,
      width: 'auto'
    }} aria-hidden data-id="element-498">
        {/* Cap */}
        <rect x="6" y="2" width="20" height="8" rx="1.4" className="fill-primary" data-id="element-499" />
        {/* Cap stripe (hairline highlight) */}
        <rect x="6" y="2" width="20" height="1.5" fill="#FFFFFF" opacity="0.25" data-id="element-500" />

        {/* Septum line */}
        <rect x="10" y="0" width="12" height="2" rx="0.5" fill={fg} opacity="0.7" data-id="element-501" />

        {/* Glass body */}
        <rect x="6" y="11" width="20" height="50" rx="2" fill="none" stroke={fg} strokeWidth="1.6" data-id="element-502" />

        {/* Meniscus line — primary green */}
        <line x1="8" y1="44" x2="24" y2="44" className="stroke-primary" strokeWidth="1.2" data-id="element-503" />

        {/* Hairline label band */}
        <line x1="6" y1="26" x2="26" y2="26" stroke={fg} strokeWidth="0.5" opacity="0.4" data-id="element-504" />
        <line x1="6" y1="36" x2="26" y2="36" stroke={fg} strokeWidth="0.5" opacity="0.4" data-id="element-505" />
      </svg>

      <svg viewBox="0 0 230 36" style={{
      height: size * 1.2,
      width: 'auto'
    }} aria-hidden data-id="element-506">
        <defs data-id="element-507">
          <style data-id="element-508">{`.l11-text { font-family: 'Geist', system-ui, sans-serif; font-weight: 500; letter-spacing: -1.4px; }`}</style>
        </defs>
        <text x="0" y="28" className="l11-text" fontSize="32" fill={fg} data-id="element-509">
          peptides
        </text>
        <text x="142" y="28" className="l11-text fill-primary" fontSize="32" data-id="element-510">
          .ml
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * L12 — Vertical Stack
 * A vertical stacked block — three pill-shaped tiles arranged
 * like a vial's vertical label. Each tile holds one element:
 * brand name, .ml unit, batch ID. The stack itself reads as
 * a sample-tube label.
 * ============================================================ */
export function LogoV12({
  className = '',
  inverted = false,
  size = 24
}: Props) {
  const fg = inverted ? '#FAF8F2' : '#0F1410';
  return <Link to="/" className={`inline-flex items-center gap-4 group ${className}`} aria-label="peptides.ml" data-id="element-511">
      <svg viewBox="0 0 130 100" style={{
      height: size * 3.4,
      width: 'auto'
    }} aria-hidden data-id="element-512">
        <defs data-id="element-513">
          <style data-id="element-514">{`
            .l12-brand { font-family: 'Geist', system-ui, sans-serif; font-weight: 600; letter-spacing: -1px; }
            .l12-unit  { font-family: 'Geist Mono', ui-monospace, monospace; font-weight: 800; letter-spacing: 3px; }
            .l12-meta  { font-family: 'Geist Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 1.6px; }
          `}</style>
        </defs>

        {/* Top tile — outlined */}
        <rect x="2" y="2" width="126" height="28" rx="14" fill="none" stroke={fg} strokeWidth="1.2" data-id="element-515" />
        <text x="65" y="22" textAnchor="middle" className="l12-brand" fontSize="18" fill={fg} data-id="element-516">
          peptides
        </text>

        {/* Middle tile — primary green filled */}
        <rect x="2" y="36" width="126" height="28" rx="14" className="fill-primary" data-id="element-517" />
        <text x="65" y="56" textAnchor="middle" className="l12-unit" fontSize="16" fill="#FFFFFF" data-id="element-518">
          .ML
        </text>

        {/* Bottom tile — outlined, mono caps batch */}
        <rect x="2" y="70" width="126" height="22" rx="11" fill="none" stroke={fg} strokeWidth="1" opacity="0.55" data-id="element-519" />
        <text x="65" y="86" textAnchor="middle" className="l12-meta" fontSize="9" fill={fg} opacity="0.7" data-id="element-520">
          RG-2024 · UK
        </text>
      </svg>

      {/* Wordmark beside the stack */}
      <svg viewBox="0 0 230 36" style={{
      height: size * 1.2,
      width: 'auto'
    }} aria-hidden data-id="element-521">
        <defs data-id="element-522">
          <style data-id="element-523">{`.l12-word { font-family: 'Geist', system-ui, sans-serif; font-weight: 500; letter-spacing: -1.4px; }`}</style>
        </defs>
        <text x="0" y="28" className="l12-word" fontSize="32" fill={fg} data-id="element-524">
          peptides
        </text>
        <text x="142" y="28" className="l12-word fill-primary" fontSize="32" data-id="element-525">
          .ml
        </text>
      </svg>
    </Link>;
}
/* ============================================================
 * Variants registry
 * ============================================================ */
export const LOGO_VARIANTS: Array<{
  id: string;
  name: string;
  description: string;
  Component: ComponentType<Props>;
}> = [{
  id: 'L1',
  name: 'L1 — The Counter',
  description: 'A custom-drawn "p" monogram whose bowl encloses a primary-green dot — the ".ml" rendered as a literal counter inside the letter. Standalone mark + tuned wordmark. Works as the brand icon (favicon, app icon, social avatar).',
  Component: LogoV1
}, {
  id: 'L2',
  name: 'L2 — The Aliquot',
  description: 'The wordmark with the period in ".ml" rendered as an oversized primary-green disc — the dot becomes the brand symbol. Pure typography + a single deliberate geometric mark. Inspired by Booking. and Recess.',
  Component: LogoV2
}, {
  id: 'L3',
  name: 'L3 — The Masthead',
  description: 'A newspaper-masthead two-line lockup. PEPTIDES in tracked display caps over a hairline rule with a primary-green centre dot, then a mono spec line beneath. Editorial gravity — feels like a serious journal title.',
  Component: LogoV3
}, {
  id: 'L4',
  name: 'L4 — Reversed Authority',
  description: 'The wordmark in white inside a primary-green rounded pill. Single high-contrast statement, perfect for stickers, packaging tape, business cards. The .ml subtly differentiated at 70% opacity.',
  Component: LogoV4
}, {
  id: 'L5',
  name: 'L5 — Specimen Card',
  description: 'A horizontal "lab ID card" lockup — hairline frame, tuned wordmark on the left, vertical divider, a mono credentials block (batch, purity, region) plus a primary-green verified-check disc on the right.',
  Component: LogoV5
}, {
  id: 'L6',
  name: 'L6 — Refined Wordmark',
  description: 'Just the wordmark, perfectly composed. Custom primary-green dot replaces the typeset period at exact x-height. Every kerning pair tuned. Nothing decorative — the discipline IS the statement.',
  Component: LogoV6
}, {
  id: 'L7',
  name: 'L7 — The Sphere',
  description: 'A precise primary-green sphere with a hairline equator and small polar tick — reads as a vial cap viewed from above, or a perfect aliquot drop. Pure geometric authority.',
  Component: LogoV7
}, {
  id: 'L8',
  name: 'L8 — The Bisect',
  description: 'The wordmark sat directly on a hairline metric line — primary green through the centre, fading to grey at the ends with terminal ticks. References calibration marks on lab glassware.',
  Component: LogoV8
}, {
  id: 'L9',
  name: 'L9 — Mono Block',
  description: 'PEPTIDES.ML in tracked mono caps, the .ML in primary green, anchored by a hairline rule and a tiny mono spec line. Vignelli-era pharma brand discipline.',
  Component: LogoV9
}, {
  id: 'L10',
  name: 'L10 — The Signature',
  description: 'A custom hand-feel signature stroke — single fluid bezier path — followed by a primary-green dot and "ml" stamped notation. References a lab-notebook entry signed by the researcher.',
  Component: LogoV10
}, {
  id: 'L11',
  name: 'L11 — The Cylinder',
  description: 'A bespoke architectural vial — minimal essential lines, primary-green crimp cap, hairline glass body, single meniscus line. Iconic without being literal chemistry.',
  Component: LogoV11
}, {
  id: 'L12',
  name: 'L12 — Vertical Stack',
  description: 'Three vertically-stacked pill tiles — outlined brand name, primary-green ".ML" unit, outlined batch ID — reading as a sample-tube label. Pairs with the wordmark.',
  Component: LogoV12
}];